// WI#62 S3 — the squad plan on the web squad page. A squad-wide entry (memberId null) is the plan
// every runner inherits; a runner's own entry for a (week, day) replaces it for them (server
// precedence: api-server/src/lib/programWeek.ts effectiveEntriesFor). All copy below is the
// Emily-approved list from Jordyn's prototype v1 README (hash d68a7d37…), with typographic
// apostrophes (’, U+2019) per Emily via Jordyn's !124 review (D2) — change a string here and
// it goes back to Emily at the new MR head.

import { sessionLabel as typeLabel } from "./sessionTypes";

export type PlanEntry = {
  id: number;
  memberId?: number | null;
  weekNumber: number;
  dayOfWeek: string;
  sessionType: string;
  targetMiles?: number | null;
};
export type PlanMember = { id: number; name: string };

export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

// #65 — the name a captain sees for a runner: the shortest form that is unambiguous in the squad.
// First name when it is unique; first name + last initial ("Alex M.") when two runners share a
// first name; the full name when the initials collide too. Without a roster it is the first name,
// exactly as before. Comparison is case-insensitive, like a person reading the names.
const fold = (s: string) => s.trim().toLowerCase();
const lastInitial = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[parts.length - 1]![0]!.toUpperCase()}.` : "";
};
export function runnerLabel(name: string, roster: readonly string[] = []): string {
  const first = firstName(name);
  const sameFirst = roster.filter((r) => fold(firstName(r)) === fold(first));
  if (sameFirst.length <= 1) return first;
  const initial = lastInitial(name);
  const sameInitial = sameFirst.filter((r) => lastInitial(r) === initial);
  if (initial && sameInitial.length <= 1) return `${first} ${initial}`;
  return name.trim().split(/\s+/).join(" ");
}
export const isRestType = (sessionType: string) => sessionType.trim().toLowerCase() === "rest";
export const runnersLabel = (n: number) => `${n} ${n === 1 ? "runner" : "runners"}`;

// Trimmed and case-folded, exactly as the server's effectiveEntriesFor keys a slot — otherwise a
// stored day with stray whitespace is an own entry to the server and inherited here (Celeste, !124).
const daySlot = (day: string) => day.trim().toLowerCase();
const sameSlot = (e: PlanEntry, week: number, day: string) =>
  e.weekNumber === week && daySlot(e.dayOfWeek) === daySlot(day);

/** Squad-wide entries for a (week, day). */
export function squadEntriesFor<T extends PlanEntry>(entries: T[], week: number, day: string): T[] {
  return entries.filter((e) => e.memberId == null && sameSlot(e, week, day)).sort((a, b) => a.id - b.id);
}

/** A runner's own entries for a (week, day). More than one only in legacy data — all are kept. */
export function ownEntriesFor<T extends PlanEntry>(entries: T[], memberId: number, week: number, day: string): T[] {
  return entries.filter((e) => e.memberId === memberId && sameSlot(e, week, day)).sort((a, b) => a.id - b.id);
}

/** Members (in roster order) who have their own session that day — they keep it when the squad session changes. */
export function membersWithOwn(entries: PlanEntry[], members: PlanMember[], week: number, day: string): PlanMember[] {
  return members.filter((m) => ownEntriesFor(entries, m.id, week, day).length > 0);
}

/**
 * The entry a new add should update in place, if any. One session per runner (or squad) per day:
 * a new add for a slot that already has one REPLACES it by editing it — never by deleting, so
 * legacy data with two entries on one day keeps both (Kayla's guard).
 */
export function entryToReplace<T extends PlanEntry>(entries: T[], memberId: number | null, week: number, day: string): T | undefined {
  return memberId === null ? squadEntriesFor(entries, week, day)[0] : ownEntriesFor(entries, memberId, week, day)[0];
}

// ---- copy (README "Copy for Emily", revised per R1–R6) ----

export const COPY = {
  wholeSquad: "Whole squad",
  addOwn: "+ own session",
  ownTag: "Own",
  yourOwnTag: "Your own",
  fromSquadPlan: "From the squad plan",
  legendSquadPlan: "Squad plan",
  legendLighter: "Lighter sessions come from the squad plan",
  legendOwn: "replaces the squad session that day",
  emptyTitle: "Plan once for the whole squad",
  emptyBody:
    "The squad plan is the default for every runner, including anyone who joins later. Add a runner’s own session only when they need something different.",
  emptyCta: "+ Add squad session",
  forLabel: "For",
  forWholeSquad: "Whole squad",
  forOneRunner: "One runner",
  addForWholeSquad: "Add for whole squad",
  addSession: "Add session",
  useSquadPlan: "use squad plan",
  useSquadPlanConfirm: "Use squad plan instead?",
  useSquadPlanYes: "Use squad plan",
  deleteConfirm: "Delete this session for everyone?",
  deleteYes: "Delete session",
  todaySquadPlan: "Today’s session · squad plan",
} as const;

export const ownTooltip = (name: string, roster?: readonly string[]) => `Replaces the squad session for ${runnerLabel(name, roster)}`;

export function modalTitle(mode: "add" | "edit", runnerName: string | null, roster?: readonly string[]): string {
  if (runnerName === null) return mode === "edit" ? "Edit Squad Session" : "Add Squad Session";
  const who = runnerLabel(runnerName, roster);
  return mode === "edit" ? `Edit ${who}’s Session` : `Add ${who}’s Own Session`;
}

function keepNote(keepers: PlanMember[], roster?: readonly string[]): string {
  if (keepers.length === 0) return "";
  const names = keepers.map((m) => runnerLabel(m.name, roster)).join(", ");
  return keepers.length === 1
    ? `${names} has their own session that day and keeps it.`
    : `${names} have their own sessions that day and keep them.`;
}

/** A run of note text; `b` marks the part the prototype sets in bold. */
export type NoteSegment = { t: string; b?: boolean };
export const noteText = (segs: NoteSegment[]) => segs.map((s) => s.t).join("");

/** The impact note shown above Session Type. */
export function impactNote(args: {
  mode: "add" | "edit";
  runnerName: string | null; // null = whole squad
  memberCount: number;
  keepers: PlanMember[]; // members with their own session that day
  squadSession: { sessionType: string; targetMiles?: number | null } | undefined;
  day: string;
  roster?: readonly string[]; // every runner's name in the squad, for #65 disambiguation
}): NoteSegment[] {
  const { mode, runnerName, memberCount, keepers, squadSession, day, roster } = args;
  if (runnerName === null) {
    const lead: NoteSegment[] =
      mode === "edit"
        ? [{ t: "Changes this session for " }, { t: runnersLabel(memberCount - keepers.length), b: true }, { t: "." }]
        : [{ t: "This becomes the default session for every runner, including anyone who joins later." }];
    const keep = keepNote(keepers, roster);
    return keep ? [...lead, { t: ` ${keep}` }] : lead;
  }
  const who = runnerLabel(runnerName, roster);
  if (squadSession && !isRestType(squadSession.sessionType)) {
    const what = `${typeLabel(squadSession.sessionType)}${squadSession.targetMiles ? ` · ${squadSession.targetMiles} mi` : ""}`;
    return [{ t: "Replaces " }, { t: what, b: true }, { t: ` from the squad plan on ${day}, for ${who} only.` }];
  }
  return [{ t: `For ${who} only. The rest of the squad isn’t affected.` }];
}

export const toastSquadSaved = (day: string) => `${day.slice(0, 3)} saved for the whole squad`;
export const toastOwnSaved = (name: string, day: string, roster?: readonly string[]) => `${runnerLabel(name, roster)}’s own ${day} session saved`;
export const toastBackOnSquadPlan = (name: string, day: string, roster?: readonly string[]) =>
  `${runnerLabel(name, roster)} is back on the squad plan for ${day}`;
export const toastSquadDeleted = (day: string) => `${day.slice(0, 3)} squad session deleted`;

// ---- removing a runner's own session (Simon's review, confirmed by Kayla) ----
// "use squad plan" is only honest when it removes the runner's LAST own entry that day. Legacy data
// can hold two; removing one of those leaves the other still overriding the squad plan, so the
// action is worded as a plain removal and nothing else is deleted automatically (Kayla's guard).

/** "revert" when this removal puts the runner back on the squad plan; "remove" when another own entry remains. */
export const ownRemovalKind = (ownCountThatDay: number): "revert" | "remove" => (ownCountThatDay > 1 ? "remove" : "revert");

export const REMOVE_COPY = {
  action: "remove this session",
  confirm: "Remove this session?",
  yes: "Remove session",
} as const;
export const toastOwnRemoved = (name: string, day: string, roster?: readonly string[]) => `${runnerLabel(name, roster)}’s ${day} session removed`;

// ---- copying several sessions at once (Copy Week) ----

/**
 * Runs one create per session and waits for ALL of them. A shared mutation's per-call callbacks
 * only fire for the latest call, so counting them never completes; settled promises do.
 */
export async function copyAll<T>(items: T[], create: (item: T) => Promise<unknown>): Promise<{ copied: T[]; failed: T[] }> {
  const results = await Promise.allSettled(items.map((item) => create(item)));
  const copied: T[] = [];
  const failed: T[] = [];
  results.forEach((r, i) => (r.status === "fulfilled" ? copied : failed).push(items[i]!));
  return { copied, failed };
}

const sessionLabel = (e: { dayOfWeek: string; sessionType: string }) => `${e.dayOfWeek.trim().slice(0, 3)} · ${typeLabel(e.sessionType)}`;

/** The Copy Week error that names what didn't copy (and says the rest did, when some did). */
export function copyWeekFailure(failed: Array<{ dayOfWeek: string; sessionType: string }>, copiedCount: number): string {
  const list = failed.map(sessionLabel).join(", ");
  return copiedCount > 0 ? `Couldn’t copy ${list}. The other sessions were copied. Try again to copy what’s left.` : `Couldn’t copy ${list}. Try again.`;
}

// #68 — Emily's copy (decision at 9402d1f7): reports the settled outcome, passive for what failed,
// and the remaining work without sounding like background progress. Every noun and verb agrees
// with its count.
const sessions = (n: number) => `${n} session${n === 1 ? "" : "s"}`;
/** Outcome toast title after a Copy Week that was cancelled while still running. */
export function copyWeekCancelledOutcome(copiedCount: number, failedCount: number, targetWeek: number): string {
  // Zero copied reads as an outcome, not a counter (Emily, at 2949620c).
  const copied = copiedCount === 0 ? `No sessions were copied to Week ${targetWeek}.` : `${sessions(copiedCount)} copied to Week ${targetWeek}.`;
  return failedCount === 0 ? copied : `${copied} ${sessions(failedCount)} ${failedCount === 1 ? "wasn’t" : "weren’t"} copied.`;
}
/** Outcome toast detail: the sessions that didn't copy, so the captain knows what to redo. */
export function copyWeekCancelledDetail(failed: Array<{ dayOfWeek: string; sessionType: string }>): string {
  return `Not copied: ${failed.map(sessionLabel).join(", ")}.`;
}
/** Idle subtitle after a partial failure (Mandy): what is left to copy, not ongoing progress. */
export function copyWeekRemaining(remaining: number, targetWeek: number): string {
  return `${sessions(remaining)} still ${remaining === 1 ? "needs" : "need"} to be copied to Week ${targetWeek}.`;
}

// #96 — Emily's copy (decision at f833e374): "scheduled" makes the outcome precise; "Your copy"
// while it is still running.
/** Appended to the outcome when some sessions were already in the target week (nothing duplicated). */
export function copyWeekAlreadyThere(count: number, targetWeek: number): string {
  return `${sessions(count)} ${count === 1 ? "was" : "were"} already scheduled in Week ${targetWeek}.`;
}
/** A second copy of the same week pair while the first is still running (e.g. after Cancel). */
export function copyWeekStillFinishing(targetWeek: number): string {
  return `Your copy to Week ${targetWeek} is still finishing. Try again in a moment.`;
}

/** Copy Week refuses its own source week: copying a week into itself doubles every session. */
export const copyWeekSameWeek = (sourceWeek: number) => `You’re copying from Week ${sourceWeek}. Choose a different week.`;
